/**
 * Ad channel connections.
 *
 * Meta retains the closed-beta system-user flow. Google and Pinterest use
 * OAuth popups so platform credentials never pass through a merchant form.
 *
 * The roadmap channels are shown but explicitly not connectable. Showing a
 * "Connect" button for a platform with no adapter behind it would be a lie the
 * merchant only discovers after their campaign silently fails to launch.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';

type LivePlatform = 'meta' | 'google' | 'tiktok' | 'reddit' | 'pinterest';
type OAuthPlatform = 'google' | 'pinterest';
type Choice = { id: string; name: string };

interface ChannelDef {
  id: LivePlatform;
  name: string;
  /** What the merchant gets that they couldn't get running it manually. */
  edge: string;
  /** Where in the platform's UI the token lives. */
  tokenHint: string;
  fields: Array<{
    key: string;
    label: string;
    secret?: boolean;
    required?: boolean;
    placeholder?: string;
  }>;
}

const CONFIGURED: ChannelDef[] = [
  {
    id: 'meta',
    name: 'Meta',
    edge: 'Fast-iteration optimiser — kills a losing creative in 3 days, not 3 weeks.',
    tokenHint: 'Business Manager → System Users → Generate Token (needs ads_management)',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: 'act_…', required: true },
      { key: 'page_id', label: 'Facebook Page ID', required: true },
      { key: 'pixel_id', label: 'Meta Pixel ID', required: true },
      { key: 'access_token', label: 'System-user access token', secret: true, required: true },
    ],
  },
  {
    id: 'google',
    name: 'Google Ads',
    edge: 'Performance Max bidding driven by your true incremental ROAS, not last-touch.',
    tokenHint: 'Google Ads API Center → developer token + OAuth refresh token',
    fields: [
      { key: 'customer_id', label: 'Customer ID', placeholder: '0000000000', required: true },
      { key: 'login_customer_id', label: 'Manager customer ID (optional)' },
      { key: 'merchant_center_id', label: 'Merchant Center ID (optional)' },
      { key: 'refresh_token', label: 'OAuth refresh token', secret: true, required: true },
    ],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    edge: 'Hooks rewritten for a 1.5-second attention window.',
    tokenHint: 'TikTok for Business → Developers → your app → access token',
    fields: [
      { key: 'advertiser_id', label: 'Advertiser ID', required: true },
      { key: 'identity_id', label: 'TikTok identity ID (optional)' },
      { key: 'pixel_code', label: 'TikTok pixel code (optional)' },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    edge: 'Patient optimiser — Pinterest converts on a slow burn, so we wait longer before judging a creative.',
    tokenHint: 'Pinterest Business → Apps → generate access token',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', required: true },
      { key: 'board_id', label: 'Board ID', required: true },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
  {
    id: 'reddit',
    name: 'Reddit',
    edge: 'Subreddit language mining plus a tone linter. Paid ads only — we never post organically, because that gets brands permanently banned.',
    tokenHint: 'Reddit Ads → Settings → API access',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: 't2_…', required: true },
      { key: 'profile_id', label: 'Profile ID (optional)' },
      { key: 'pixel_id', label: 'Reddit Pixel ID (optional)' },
      { key: 'access_token', label: 'Access token', secret: true, required: true },
    ],
  },
];

const ROADMAP = [
  { name: 'Google Ads', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'TikTok', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'Pinterest', why: 'Integration is being production-tested and is not enabled for campaign writes.' },
  { name: 'Reddit Ads', why: 'Intelligence remains available; paid campaign writes are not enabled.' },
  { name: 'Snapchat', why: 'Cheap reach for under-25 audiences your Meta CPMs price you out of.' },
  { name: 'YouTube Shorts', why: 'Reuses your TikTok cuts with no extra production.' },
  { name: 'X', why: 'Cheapest CPMs of the majors — worth testing, rarely worth scaling.' },
  { name: 'Spotify', why: 'Audio hooks generated from your best-performing written hooks.' },
  { name: 'Amazon Ads', why: 'Only worth it if you also sell on Amazon.' },
  { name: 'LinkedIn', why: 'B2B only. High CPMs, poor fit for most Shopify stores.' },
];

type Conn = { platform: string; external_id: string; status: string; connected_at: number };
type Capabilities = Record<'meta' | 'google' | 'pinterest', { enabled: boolean; configured: boolean }>;

export default function Channels() {
  const [conns, setConns] = useState<Conn[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<LivePlatform | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities>({
    meta: { enabled: true, configured: true },
    google: { enabled: false, configured: false },
    pinterest: { enabled: false, configured: false },
  });
  const [setupOptions, setSetupOptions] = useState<Record<string, Choice[]> | null>(null);

  const load = useCallback(async () => {
    try {
      const [connections, platformCapabilities] = await Promise.all([
        api.connections(),
        api.platformCapabilities(),
      ]);
      setConns(connections);
      setCapabilities(platformCapabilities);
    } catch {
      setConns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const receiveOAuth = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = event.data as { type?: string; platform?: OAuthPlatform };
      if (message.type !== 'scaleads:oauth' || !message.platform) return;
      void (async () => {
        setBusy(message.platform!);
        try {
          await load();
          setSetupOptions(await api.platformSetupOptions(message.platform!));
          setOpenForm(message.platform!);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Authorization completed, but account setup could not be loaded.');
        } finally {
          setBusy(null);
        }
      })();
    };
    window.addEventListener('message', receiveOAuth);
    return () => window.removeEventListener('message', receiveOAuth);
  }, [load]);

  const active = (id: string) => conns.find((c) => c.platform === id && c.status === 'active');
  const connection = (id: string) => conns.find((c) => c.platform === id);
  const live = CONFIGURED.filter((channel) => {
    if (channel.id === 'meta') return capabilities.meta.enabled;
    if (channel.id !== 'google' && channel.id !== 'pinterest') return false;
    return capabilities[channel.id].enabled && capabilities[channel.id].configured;
  });

  async function startOAuth(platform: OAuthPlatform) {
    setBusy(platform);
    setError(null);
    try {
      const { authorizationUrl } = await api.startPlatformOAuth(platform);
      const popup = window.open(authorizationUrl, 'scaleads_platform_oauth', 'popup,width=760,height=760');
      if (!popup) throw new Error('Popup blocked');
      popup.focus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Allow popups, then try connecting again.');
    } finally {
      setBusy(null);
    }
  }

  async function finishOAuth(platform: OAuthPlatform, selection: Record<string, string>) {
    setBusy(platform);
    setError(null);
    try {
      await api.finishPlatformSetup(platform, selection);
      setOpenForm(null);
      setSetupOptions(null);
      await load();
      setToast(`${platform === 'google' ? 'Google Ads' : 'Pinterest'} connected`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Account validation failed.');
    } finally {
      setBusy(null);
    }
  }

  async function disconnect(platform: string) {
    setBusy(platform);
    setError(null);
    try {
      await api.disconnectPlatform(platform);
      setConns((c) => c.filter((x) => x.platform !== platform));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not disconnect.');
    } finally {
      setBusy(null);
    }
  }

  async function connect(
    platform: LivePlatform,
    externalId: string,
    credentials: Record<string, string>,
  ) {
    setBusy(platform);
    setError(null);
    try {
      await api.connectPlatform({ platform, externalId, credentials });
      setOpenForm(null);
      setConns((c) => [
        ...c.filter((x) => x.platform !== platform),
        { platform, external_id: externalId, status: 'active', connected_at: Date.now() },
      ]);
      setToast(`${platform} connected`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not connect that account.');
    } finally {
      setBusy(null);
    }
  }

  async function resync() {
    setResyncing(true);
    try {
      await api.resync();
      setToast('Product resync queued');
      setTimeout(() => setToast(null), 3000);
    } catch {
      setError('Resync could not be queued.');
    } finally {
      setResyncing(false);
    }
  }

  const connectedCount = live.filter((c) => active(c.id)).length;

  return (
    <>
      <p className="eyebrow">Setup</p>
      <h1 className="pg-title">Ad channels</h1>
      <p className="pg-sub">
        Connect an approved channel to create paused campaigns, then pause, resume and re-budget only with merchant approval. Every
        automated change is logged, capped at ±30% per step, and reversible for 24 hours. Disconnect
        stops all automated spend immediately.
      </p>

      <div className="row gap-2 mb-3">
        <span className="badge b-active">
          {connectedCount} of {live.length} connected
        </span>
        <button className="btn sm" onClick={() => void resync()} disabled={resyncing}>
          {resyncing ? 'Queuing…' : 'Resync Shopify products'}
        </button>
      </div>

      {error && <p className="err-line">{error}</p>}

      <h4 className="tier-head">
        Available now<span className="meta">approved, configured channels only</span>
      </h4>

      {loading ? (
        <div className="empty">Loading connections…</div>
      ) : (
        <div className="channel-grid">
          {live.map((c) => {
            const conn = active(c.id);
            const pending = connection(c.id)?.status === 'setup_required';
            return (
              <article key={c.id} className={`card card-p channel ${conn ? 'on' : ''}`}>
                <div className="channel-head">
                  <h3>{c.name}</h3>
                  <span className={`badge ${conn ? 'b-active' : ''}`}>
                    {conn ? 'connected' : 'not connected'}
                  </span>
                </div>
                <p className="channel-edge">{c.edge}</p>

                {openForm === c.id && (c.id === 'google' || c.id === 'pinterest') ? (
                  <OAuthSetupForm
                    platform={c.id}
                    options={setupOptions ?? {}}
                    busy={busy === c.id}
                    onCancel={() => { setOpenForm(null); setSetupOptions(null); }}
                    onSubmit={(selection) => void finishOAuth(c.id as OAuthPlatform, selection)}
                  />
                ) : openForm === c.id ? (
                  <ConnectForm
                    def={c}
                    busy={busy === c.id}
                    onCancel={() => setOpenForm(null)}
                    onSubmit={(id, credentials) => void connect(c.id, id, credentials)}
                  />
                ) : (
                  <div className="channel-foot">
                    {conn ? (
                      <>
                        <span className="meta">
                          since {new Date(conn.connected_at).toLocaleDateString('en-AU')}
                        </span>
                        <button
                          className="btn sm danger"
                          onClick={() => void disconnect(c.id)}
                          disabled={busy === c.id}
                        >
                          {busy === c.id ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      </>
                    ) : pending ? (
                      <button className="btn sm brand" onClick={() => {
                        void api.platformSetupOptions(c.id as OAuthPlatform).then((options) => {
                          setSetupOptions(options);
                          setOpenForm(c.id);
                        });
                      }}>
                        Finish {c.name} setup
                      </button>
                    ) : (
                      <button className="btn sm brand" onClick={() => {
                        if (c.id === 'google' || c.id === 'pinterest') void startOAuth(c.id);
                        else setOpenForm(c.id);
                      }} disabled={busy === c.id}>
                        {busy === c.id ? 'Opening…' : `Connect ${c.name}`}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <h4 className="tier-head">
        On the roadmap<span className="meta">no adapter yet — not connectable</span>
      </h4>
      <div className="channel-grid">
        {ROADMAP.filter((roadmap) => !live.some((channel) => channel.name === roadmap.name)).map((r) => (
          <article key={r.name} className="card card-p channel roadmap">
            <div className="channel-head">
              <h3>{r.name}</h3>
              <span className="badge">planned</span>
            </div>
            <p className="channel-edge">{r.why}</p>
          </article>
        ))}
      </div>

      <p className="footnote">
        Tokens are encrypted at rest with AES-256-GCM and never returned by the API — not even to
        you. If you rotate a token in the platform, reconnect here. Campaigns already live keep
        running under the platform's own settings after you disconnect; we don't delete them,
        because deleting your ad history isn't ours to do.
      </p>

      {toast && <div className="toast show">{toast}</div>}
    </>
  );
}

function OAuthSetupForm({
  platform,
  options,
  busy,
  onCancel,
  onSubmit,
}: {
  platform: OAuthPlatform;
  options: Record<string, Choice[]>;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (selection: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const field = (key: string, label: string, choices?: Choice[], placeholder?: string) => (
    <label className="field" key={key}>
      <span>{label}</span>
      {choices?.length ? (
        <select className="input" value={values[key] ?? ''} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))} required>
          <option value="">Choose…</option>
          {choices.map((choice) => <option key={choice.id} value={choice.id}>{choice.name} ({choice.id})</option>)}
        </select>
      ) : (
        <input className="input" value={values[key] ?? ''} placeholder={placeholder} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))} required />
      )}
    </label>
  );
  const required = platform === 'google'
    ? ['customerId', 'merchantCenterId', 'conversionActionId']
    : ['adAccountId', 'boardId'];
  const complete = required.every((key) => values[key]?.trim());

  return (
    <form className="connect-form" onSubmit={(event) => { event.preventDefault(); if (complete) onSubmit(values); }}>
      {platform === 'google' ? (
        <>
          {field('customerId', 'Google Ads account', options.customers, 'Customer ID')}
          {field('merchantCenterId', 'Merchant Center account', options.merchants, 'Merchant Center ID')}
          {field('conversionActionId', 'Purchase conversion action ID', undefined, 'Conversion action ID')}
          <label className="field"><span>Manager customer ID (optional)</span><input className="input" value={values.loginCustomerId ?? ''} onChange={(event) => setValues((current) => ({ ...current, loginCustomerId: event.target.value }))} /></label>
        </>
      ) : (
        <>
          {field('adAccountId', 'Pinterest ad account', options.adAccounts, 'Ad account ID')}
          {field('boardId', 'Pinterest board', options.boards, 'Board ID')}
        </>
      )}
      <p className="footnote">Campaign creation remains paused until you explicitly approve activation.</p>
      <div className="row gap-2">
        <button type="submit" className="btn sm brand" disabled={busy || !complete}>{busy ? 'Validating…' : 'Finish setup'}</button>
        <button type="button" className="btn sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function ConnectForm({
  def,
  busy,
  onCancel,
  onSubmit,
}: {
  def: ChannelDef;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (externalId: string, credentials: Record<string, string>) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const requiredComplete = def.fields
    .filter((field) => field.required)
    .every((field) => values[field.key]?.trim());

  return (
    <form
      className="connect-form"
      onSubmit={(e) => {
        e.preventDefault();
        const credentials = Object.fromEntries(
          Object.entries(values)
            .map(([key, value]) => [key, value.trim()])
            .filter(([, value]) => value),
        );
        const identifier =
          credentials.ad_account_id ?? credentials.customer_id ?? credentials.advertiser_id;
        if (!identifier) return;
        onSubmit(identifier, credentials);
      }}
    >
      {def.fields.map((field) => (
        <label className="field" key={field.key}>
          <span>{field.label}</span>
          <input
            className="input"
            type={field.secret ? 'password' : 'text'}
            value={values[field.key] ?? ''}
            placeholder={field.placeholder}
            onChange={(e) => setValues((current) => ({ ...current, [field.key]: e.target.value }))}
            autoComplete="off"
            required={field.required}
          />
        </label>
      ))}
      <p className="footnote" style={{ marginTop: 0 }}>
        {def.tokenHint}
      </p>
      <div className="row gap-2">
        <button type="submit" className="btn sm brand" disabled={busy || !requiredComplete}>
          {busy ? 'Verifying…' : 'Connect'}
        </button>
        <button type="button" className="btn sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
